import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/ui/VideoCard";
import Pagination from "@/components/ui/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { isVideoType, searchVideos, splitIntoPages, totalPages } from "@/lib/videos";
import type { VideoType } from "@/types/video";

const PAGE_SIZE = 24;

const TYPE_FILTERS: Array<{ key: VideoType | "all"; label: string; icon: string }> = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "movie", label: "电影", icon: "ri-film-line" },
  { key: "tv", label: "电视剧", icon: "ri-tv-2-line" },
  { key: "variety", label: "综艺", icon: "ri-mic-line" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword")?.trim() ?? "";
  const typeParam = searchParams.get("type") ?? "all";

  const [input, setInput] = useState(keyword);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const type: VideoType | "all" = isVideoType(typeParam) ? typeParam : "all";

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 密桃视频` : "搜索视频 - 密桃视频",
    description: keyword
      ? `密桃视频中包含“${keyword}”的视频搜索结果，可按电影、电视剧、综艺分类筛选。`
      : "输入关键词，搜索密桃视频平台上的电影、电视剧与综艺内容。",
    keywords: "搜索视频,密桃视频,影视搜索,电影,电视剧,综艺",
  });

  const results = useMemo(() => {
    if (!keyword) return [];
    return searchVideos(keyword, type === "all" ? undefined : type);
  }, [keyword, type]);

  const pages = totalPages(results.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const visible = splitIntoPages(results, currentPage, PAGE_SIZE);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    const next = new URLSearchParams();
    if (trimmed) next.set("keyword", trimmed);
    if (type !== "all") next.set("type", type);
    setSearchParams(next);
  };

  const changeType = (next: VideoType | "all") => {
    const params = new URLSearchParams();
    if (keyword) params.set("keyword", keyword);
    if (next !== "all") params.set("type", next);
    setSearchParams(params);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 lg:px-8 md:py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <div className="rounded-lg border border-background-200/70 bg-background-100/70 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          视频搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入关键词，快速搜索视频标题与主演信息
        </p>
        <form onSubmit={submitSearch} className="mt-5 flex flex-col gap-3 sm:flex-row" role="search">
          <div className="relative flex flex-1 items-center">
            <i className="ri-search-line absolute left-3.5 text-lg text-foreground-400 pointer-events-none"></i>
            <input
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：星河、悬疑、音乐……"
              aria-label="搜索视频关键词"
              className="h-12 w-full rounded-md border border-background-200/80 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="h-12 rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </div>

      {keyword && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-foreground-700">
              “<span className="font-semibold text-primary-600">{keyword}</span>” 的搜索结果，共
              <span className="font-semibold text-primary-600"> {results.length} </span>条
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-1">
            {TYPE_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => changeType(item.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                  type === item.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                <i className={item.icon}></i>
                {item.label}
              </button>
            ))}
          </div>

          <div className="mt-6">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <p className="mt-5 text-base font-medium text-foreground-800">
                  没有找到与“{keyword}”相关的内容
                </p>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或者返回首页浏览精选视频
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {visible.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                <Pagination current={currentPage} total={pages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      )}

      {!keyword && (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-search-line text-3xl"></i>
          </span>
          <p className="mt-5 text-base font-medium text-foreground-800">输入关键词开始搜索</p>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            支持按视频标题与主演搜索，例如“星河”“悬疑”“音乐”，找到你想了解的内容。
          </p>
        </div>
      )}
    </div>
  );
}